import * as React from 'react';
import { ReactElement, useState } from 'react';
import BasicModal from 'components/common/modal/BasicModal';
import styled from 'styled-components';
import { Button, Grid, Input, TextField, Typography } from '@material-ui/core';
import { ColorText } from 'components/plansales/billing/detail/invoice/InvoiceTableRenderer';
import { adjustCredit } from 'api/backorder';
import { AxiosError } from 'axios';
import ConfirmModal from 'components/common/modal/ConfirmModal';
import ErrorModal from 'components/common/modal/ErrorModal';
import { toMantissaWithThousandComma } from 'modules/utils/number';
import NumberFormat, { NumberFormatValues } from 'react-number-format';

interface Props {
  groupId: string;
  currentCredit: number;
  onClose: () => void;
}

const AddCreditModal = ({ groupId, currentCredit, onClose }: Props): ReactElement => {
  const [commentValue, setCommentValue] = useState<string>('');
  const [addCreditValue, setAddCreditValue] = useState<number>(0);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<{ code: number | string; message: string } | null>(
    null,
  );
  const expectedValue =
    currentCredit +
    (!isNaN(Number(addCreditValue)) ? parseFloat(Number(addCreditValue).toString()) : 0);

  const onConfirm = async () => {
    closeConfirmModal();
    setIsLoading(true);
    try {
      onClose();
      await adjustCredit({
        groupId,
        comment: commentValue,
        currentCredit,
        additionalCredit: addCreditValue as number,
      });
      window.location.reload();
    } catch (e) {
      const error = e as AxiosError;
      if (!error.response?.data) {
        setErrorStatus({
          code: error.code ?? 500,
          message: error.message,
        });
        return;
      }
      setErrorStatus({
        code: error.response.data.resultCode,
        message: error.response.data.resultMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (value: number): void => {
    setAddCreditValue(value as number);
  };

  const openConfirmModal = () => {
    setIsConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  return (
    <>
      <BasicModal
        open={!isConfirmModalOpen}
        onClose={onClose}
        title={'Add Credits'}
        actionButton={
          <>
            <Button variant={'text'} color={'default'} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={'text'}
              color={'primary'}
              onClick={openConfirmModal}
              disabled={!commentValue || expectedValue === currentCredit || isLoading}
            >
              Confirm
            </Button>
          </>
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={6}>
            Current Credit Balance
          </Grid>
          <Grid item xs={6}>
            <Input value={toMantissaWithThousandComma(currentCredit)} disabled />
            <span>Credits</span>
          </Grid>
          <Grid item xs={6}>
            Add Credits
          </Grid>
          <Grid item xs={6}>
            <NumberFormat
              value={addCreditValue}
              thousandSeparator
              decimalScale={2}
              customInput={Input}
              onValueChange={(value: NumberFormatValues) => {
                if (!value.floatValue) return;
                onChange(value.floatValue);
              }}
              onBlur={({ target: { value } }: React.FocusEvent<HTMLInputElement>) => {
                const floatNumber = Number(value.replace(/,/gi, ''));
                onChange(Math.floor(floatNumber * 100) / 100);
              }}
              allowNegative
            />
            <span>Credits</span>
          </Grid>
          <Line item xs={12} />
          <Grid item xs={6}>
            Expected Credits
          </Grid>
          <Grid item xs={6}>
            <Input value={toMantissaWithThousandComma(expectedValue)} disabled />
            <span>Credits</span>
          </Grid>
        </Grid>

        <CommentWrapper>
          <Typography>
            Comment <ColorText color={'red'}> *</ColorText>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant={'outlined'}
            value={commentValue}
            onChange={e => {
              setCommentValue(e.target.value);
            }}
            inputProps={{ maxLength: 500 }}
          />
        </CommentWrapper>
      </BasicModal>
      <ConfirmModal
        maxWidth={'sm'}
        description={<div>Are you sure you want to add Credits for this Group?</div>}
        open={isConfirmModalOpen}
        onClose={closeConfirmModal}
        onConfirm={onConfirm}
      />
      {errorStatus && (
        <ErrorModal
          isOpen={true}
          onClose={() => setErrorStatus(null)}
          content={errorStatus.message}
        />
      )}
    </>
  );
};

export default AddCreditModal;

const CommentWrapper = styled.div`
  margin: ${props => props.theme.spacing(3)}px 0px;
`;
const Line = styled(Grid)`
  padding: 0px !important;
  margin: ${props => props.theme.spacing(3)}px 0px;
  border: 0.25px solid rgba(0, 0, 0, 0.4);
`;
